import React, { useEffect, useRef, useState } from 'react';
import { Typography, Tooltip, ClickAwayListener, useMediaQuery, useTheme } from '@mui/material';
import { useTranslate } from 'react-admin';
import PropTypes from 'prop-types';
import { Palette } from '@/themes/themeOverrides/Palette';

const CustomTypographyWithTooltip = ({ text }) => {
    const translate = useTranslate();
    const textRef = useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [open, setOpen] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const isEmpty = !text?.trim();

    useEffect(() => {
        const el = textRef.current;
        if (el) {
            const computedStyles = getComputedStyle(el);
            const lineHeight = Number.parseFloat(computedStyles.lineHeight || '20');
            const lines = Math.round(el.scrollHeight / lineHeight);
            setIsOverflowing(lines > 2);
        }
    }, [text]);

    const handleTooltipToggle = () => {
        if (isMobile && isOverflowing) {
            setOpen(prev => !prev);
        }
    };

    const handleClickAway = () => {
        if (isMobile) {
            setOpen(false);
        }
    };

    const typography = (
        <Typography
            ref={textRef}
            variant='body2'
            onClick={handleTooltipToggle}
            className={`${isEmpty ? 'empty' : isOverflowing ? 'has-tooltip' : 'no-tooltip'}`}
            sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'normal',
                lineHeight: '20px',
                maxHeight: '40px',
                width: '326px',
                '&.has-tooltip': {
                    '&:hover': {
                        backgroundColor: Palette.gray.gray75,
                        outline: `20px solid ${Palette.gray.gray75}`,
                    },
                },
                '&.empty': {
                    width: 'max-content',
                },
            }}
        >
            {text || translate('common.shortage')}
        </Typography>
    );

    return isOverflowing ? (
        <ClickAwayListener onClickAway={handleClickAway}>
            <div style={{ display: 'inline-block', width: '100%' }}>
                <Tooltip
                    title={text}
                    arrow
                    placement='bottom'
                    classes={{ tooltip: 'tooltip-white' }}
                    open={isMobile ? open : undefined}
                    disableHoverListener={isMobile}
                    disableFocusListener={isMobile}
                    disableTouchListener={isMobile}
                >
                    <span>{typography}</span>
                </Tooltip>
            </div>
        </ClickAwayListener>
    ) : (
        typography
    );
};

CustomTypographyWithTooltip.propTypes = {
    text: PropTypes.string,
    maxLines: PropTypes.number,
    className: PropTypes.string,
};

export default CustomTypographyWithTooltip;
